/**
 * "Yours" — the meta measured against the trainer's own Pokémon.
 *
 * The other two modes are references: what is good. This one is advice: what
 * is good THAT YOU HAVE, and what is worth chasing next.
 *
 * READ OFF MY POKÉMON, NOT THE DEX (2026-09-10 owner direction). The dex only
 * knows a species was caught once; a My Pokémon record knows the copy: IVs,
 * CP, level, shadow, form. So ownership here means "a copy of it is in your
 * box", and the block at the top names the best copy you hold inside each
 * meta's top 100, judged on what it becomes (lib/yoursMetaBest.ts). Moves are
 * still the honest gap: the ranked moveset is printed beside every counter
 * because a scan reads moves only sometimes.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Lock, Sparkles } from "lucide-react";

import { CaughtSprite } from "@/components/CaughtSprite";
import { LeagueBall } from "@/components/LeagueBall";
import { MetaSearchButton } from "./MetaSearchButton";
import { ProLock } from "@/components/ProLock";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import { useMyPokemonRecords } from "@/lib/myPokemonStore";
import { myPokemonDisplayName } from "@/lib/myPokemonFilter";
import {
  ownsFromRecords,
  yourPvpPicks,
  yourRaidPicks,
  type YourPvpPick,
  type YourRaidPick,
  type YoursLeagueKey,
} from "@/lib/yoursMetaBest";
import {
  bossCoverage,
  coverageSummary,
  perTypeAttackerRankings,
  perTypeRankings,
  shadowUpgrades,
  typeCoverage,
  type BossCoverage,
  type OwnsFn,
  type ShadowUpgrade,
  type TypeCoverage,
  type TypeCoverageMode,
} from "@/lib/metaCoverage";
import {
  RAID_TIERS_BIGGEST_FIRST,
  raidTierLabel,
  type RaidBossDto,
  type RaidTierKey,
} from "@/lib/raidRoster";
import { MoveChip, typeColor, typeLabel } from "./metaChrome";
import { SpineDot } from "@/components/SectionHeadingDot";

interface RaidsResponse {
  tiers?: Partial<Record<string, RaidBossDto[]>>;
}

/** How deep into the counter list "you own N of M" counts. */
const DEPTH = 10;

// Biggest first. Shared with the Raids panel so the two cannot disagree.
const TIERS_BIGGEST_FIRST = RAID_TIERS_BIGGEST_FIRST;

export function YoursMetaPanel({ onExploreRaids }: { onExploreRaids?: () => void }) {
  // THE FEELERS. openPaywall records `source` against the intent (migration
  // 0071), so a purchase that started here is attributable to here rather than
  // landing in the unattributable pile. "meta-podium" exists for exactly this
  // call site; the union is closed so it could not be added by accident.
  const { tier, openPaywall } = useSession();
  const isPro = hasProAccess(tier);
  const upsellFromPodium = () =>
    openPaywall("meta-podium", "See the full top ten for every type with Pro");

  // The box, live: a record added or edited anywhere in the app shows up
  // here without a reload (useSyncExternalStore over the My Pokémon store).
  const records = useMyPokemonRecords();

  const [selectedType, setSelectedType] = useState<string | null>(null);
  // Which reading of the type grid is up (2026-09-17 owner ask): the ten
  // best attackers OF each type, or the ten best AGAINST each boss type. The
  // counters list opened as "Fighting bosses" and read as the wrong thing, so
  // both lists now sit behind tabs that say which is which.
  const [typeMode, setTypeMode] = useState<TypeCoverageMode>("counters");

  const raidsQ = useQuery<RaidsResponse>({
    queryKey: ["/api/events/raids"],
    staleTime: 30 * 60_000,
  });

  const owns: OwnsFn = useMemo(() => ownsFromRecords(records), [records]);

  // The best copies for each meta's top 100. Computed only for Pro: the free
  // page shows the lock, and the sweep behind the destiny read is not free.
  const pvpPicks = useMemo(
    () =>
      isPro
        ? (["great", "ultra", "master"] as const).map((league) => ({
            league,
            picks: yourPvpPicks(records, league),
          }))
        : [],
    [records, isPro],
  );
  const raidPicks = useMemo(() => (isPro ? yourRaidPicks(records) : []), [records, isPro]);

  const bosses = useMemo(() => {
    const tiers = raidsQ.data?.tiers ?? {};
    return TIERS_BIGGEST_FIRST.flatMap((tier) =>
      (tiers[tier] ?? []).map((boss) => ({ boss, tier })),
    );
  }, [raidsQ.data]);

  const coverage = useMemo(
    () =>
      bosses
        .map(({ boss, tier }) => ({ tier, cov: bossCoverage(boss, owns, DEPTH) }))
        .filter((x): x is { tier: RaidTierKey; cov: BossCoverage } => x.cov != null),
    [bosses, owns],
  );

  // ONE set of per-type rankings behind both the grid and the upgrade
  // ordering: they are questions about the same eighteen rankings, and two
  // passes would be twice the work for two answers that have to agree.
  const rankings = useMemo(() => perTypeRankings(owns), [owns]);
  const attackerRankings = useMemo(() => perTypeAttackerRankings(owns), [owns]);
  // The grid follows the tab; the shadow-upgrade ordering stays on the
  // counters, which is the question it answers ("what would plug a hole").
  const grid = useMemo(
    () => typeCoverage(typeMode === "counters" ? rankings : attackerRankings),
    [typeMode, rankings, attackerRankings],
  );
  const upgrades = useMemo(() => shadowUpgrades(owns, rankings), [owns, rankings]);

  // Bundled by tier so the list reads as "here are your Megas, here are your
  // 5-stars", rather than one undifferentiated run of bosses.
  const byTier = useMemo(
    () =>
      TIERS_BIGGEST_FIRST.map((tier) => ({
        tier,
        items: coverage.filter((c) => c.tier === tier),
      })).filter((g) => g.items.length > 0),
    [coverage],
  );

  // HAS THIS TRAINER ANY POKÉMON AT ALL? An empty box renders as eighteen
  // zeroes and a wall of "you own none of the top ten", which is accurate and
  // useless — it reads as a verdict on the trainer rather than as a page that
  // has nothing to work with yet.
  //
  // Deliberately distinct from "owns no top counters", which is a REAL state
  // worth showing: a trainer with a few hundred Pokémon and nothing meta is
  // exactly who the grid is for, and hiding it would be hiding the answer.
  const hasAnyCatches = records.some((r) => r.speciesId != null);
  const summary = useMemo(() => coverageSummary(grid), [grid]);
  const selected = grid.find((t) => t.type === selectedType) ?? null;

  return (
    <>
      {/* Each block draws its OWN spine dot rather than relying on DotRail's
          `data-dotrail-dot`: the rail only reads markers off its direct
          children, and these sections are inside this component, so the markers
          it used to carry were never seen and the whole panel sat dotless
          against the rail. See SpineDot. */}
      <section className="relative v2-neon-card-soft p-3" data-testid="yours-preamble">
        <SpineDot />
        <p className="text-[11.5px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Your My Pokémon compared against the meta: every copy&rsquo;s CP, IVs and level count,
          judged on what it becomes. <strong style={{ color: "var(--text-primary)" }}>Make sure
          your Pokémon knows the move set shown.</strong>
        </p>
      </section>

      {!hasAnyCatches && (
        <section className="relative mt-3 v2-neon-card-soft p-4" data-testid="yours-no-dex">
          <SpineDot />
          <p className="text-[12.5px] font-bold" style={{ color: "var(--text-ink-bright)" }}>
            Nothing in My Pokémon yet.
          </p>
          <p className="mt-1.5 text-[11.5px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            This page compares the Pokémon you hold against the rankings, so it needs a few in
            My Pokémon to read. Scan or add some and it will fill in on its own: your best for
            each meta, where you are strong, which of this week&rsquo;s bosses you can handle,
            and what is worth chasing.
          </p>
          {onExploreRaids && (
            <button
              type="button"
              onClick={onExploreRaids}
              data-testid="yours-goto-raids"
              className="mt-3 rounded-full px-3 py-1.5 text-[11px] font-bold"
              style={{
                background: "var(--accent-fill-glow-faint)",
                border: "1px solid var(--accent-fill-glow)",
                color: "var(--brand-green-on-light)",
              }}
            >
              See the raid rankings instead
            </button>
          )}
        </section>
      )}

      {hasAnyCatches && (
      <>
      {/* 0. YOUR BEST IN THE META (2026-09-10 owner ask, Pro). The one block
             the dex could never draw: the actual copies, ranked where they
             stand in each top 100, judged on what they become. */}
      <section className="relative mt-3" data-testid="section-yours-best">
        <SpineDot />
        <h2 className="text-[11px] font-black uppercase tracking-wider px-1 mb-1" style={{ color: "var(--accent-fill)" }}>
          Your best in the meta
        </h2>
        <p className="text-[10.5px] px-1 mb-2 leading-snug" style={{ color: "var(--text-secondary)" }}>
          The strongest Pokémon you hold inside each top 100. A copy that gets there by evolving
          shows what it becomes.
        </p>
        {isPro ? (
          <div className="space-y-2.5">
            {pvpPicks.map(({ league, picks }) => (
              <YourLeagueBest key={league} league={league} picks={picks} />
            ))}
            <YourRaidBest picks={raidPicks} />
          </div>
        ) : (
          <ProLock
            feature="meta-yours"
            source="meta-yours"
            blurb="Pro names the best Pokémon you hold for every league's top 100 and the raid top 100, judged on what each one becomes."
          />
        )}
      </section>

      {/* 1. TYPE COVERAGE. The shape of the collection, first — "where am I
             thin" is the standing question, and a type with nothing in it is a
             week of raids you will struggle with the moment one appears. The
             two sections below are what to DO about it: this week, and next. */}
      <section className="relative mt-3" data-testid="section-yours-types">
        <SpineDot />
        <h2 className="text-[11px] font-black uppercase tracking-wider px-1 mb-1" style={{ color: "var(--accent-fill)" }}>
          Type coverage
        </h2>
        {/* THE TWO READINGS OF "BEST OF A TYPE", as tabs. Both are a top ten
            per type, marked against the box; only the question differs, and
            the tab, the explainer, every cell's tooltip and the detail heading
            all say which one is up. */}
        <div className="px-1 mb-2 flex gap-1.5" role="tablist" aria-label="Which top ten" data-testid="yours-type-mode">
          {(
            [
              { id: "attackers", label: "Best per type" },
              { id: "counters", label: "Best counters" },
            ] as const
          ).map((tab) => {
            const active = typeMode === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTypeMode(tab.id)}
                data-testid={`yours-type-mode-${tab.id}`}
                className="rounded-full px-3 py-1.5 text-[11px] font-bold"
                style={{
                  background: active ? "var(--accent-fill)" : "var(--surface-sunken)",
                  color: active ? "var(--text-on-accent)" : "var(--text-secondary)",
                  border: `1px solid ${active ? "var(--accent-fill)" : "var(--border-subtle)"}`,
                  minHeight: 32,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <p className="text-[10.5px] px-1 mb-1 leading-snug font-bold" style={{ color: "var(--text-primary)" }} data-testid="yours-type-mode-explainer">
          {typeMode === "attackers"
            ? "The ten best attackers OF each type, and which of them you own."
            : "The ten best attackers AGAINST each type of boss, and which of them you own."}
        </p>
        <p className="text-[10.5px] px-1 mb-2 leading-snug" style={{ color: "var(--text-secondary)" }} data-testid="yours-coverage-summary">
          {summary.complete ? (
            <>You own a top-10 {typeMode === "attackers" ? "attacker of" : "counter for"} <strong style={{ color: "var(--accent-fill)" }}>every type</strong>. The number in each cell is how deep that goes.</>
          ) : (
            summary.covered === 0 ? (
              <>
                None of your catches are in a type&rsquo;s top ten yet. The numbers below are what
                to aim at. Tap one to see what to chase first.
              </>
            ) : (
            <>
              You have something for{" "}
              <strong style={{ color: "var(--text-secondary)" }}>
                {summary.covered} of {summary.total}
              </strong>{" "}
              {typeMode === "attackers" ? "types" : "boss types"}. Thinnest:{" "}
              {summary.weakest.map((w, i) => (
                <span key={w.type}>
                  {i > 0 ? ", " : ""}
                  <strong style={{ color: "var(--warning-text)" }}>{typeLabel(w.type)}</strong>
                </span>
              ))}
              .
            </>
            )
          )}
        </p>

        <div className="grid grid-cols-3 gap-1.5" data-testid="yours-type-grid">
          {grid.map((t) => (
            <TypeCell
              key={t.type}
              cov={t}
              mode={typeMode}
              selected={selectedType === t.type}
              onSelect={() => setSelectedType(selectedType === t.type ? null : t.type)}
            />
          ))}
        </div>

        {selected && <TypeDetail cov={selected} mode={typeMode} isPro={isPro} onUpsell={upsellFromPodium} />}
      </section>

      {/* 2. THIS WEEK'S RAIDS. The time-bounded half: not "what is good" but
             "can I do the raid that is up right now, and with what". Reads
             second because the grid above has just said where the holes are,
             and this is where one of them is about to matter. */}
      <section className="relative mt-5" data-testid="section-yours-raids">
        <SpineDot />
        <h2 className="text-[11px] font-black uppercase tracking-wider px-1 mb-2" style={{ color: "var(--accent-fill)" }}>
          Raids right now
        </h2>

        {raidsQ.isLoading && (
          <p className="text-[11px] px-1" style={{ color: "var(--text-secondary)" }} data-testid="yours-raids-loading">
            Loading the raid roster…
          </p>
        )}

        {!raidsQ.isLoading && coverage.length === 0 && (
          <p
            className="v2-neon-card-soft p-4 text-[11.5px]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="yours-raids-empty"
          >
            No bosses are listed right now. The rankings in the Raids tab still work.
          </p>
        )}

        <div className="space-y-4" data-testid="yours-bosses">
          {byTier.map(({ tier, items }) => (
            <div key={tier} data-testid={`yours-tier-${tier}`}>
              <p
                className="text-[9.5px] font-black uppercase tracking-wider px-1 mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                {raidTierLabel(tier)}
              </p>
              <ul className="space-y-2">
                {items.map(({ cov }) => (
                  <li key={`${tier}-${cov.boss.name}`}>
                    <BossCard tier={tier} cov={cov} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 3. SHADOW UPGRADES. The longer game, so it sits last: the cleanest
             claim on the page (the dex answers both halves exactly) and a
             concrete +20% attack on something already in the bag. */}
      <section className="relative mt-5" data-testid="section-yours-shadows">
        <SpineDot />
        <h2 className="text-[11px] font-black uppercase tracking-wider px-1 mb-1" style={{ color: "var(--accent-fill)" }}>
          Shadow upgrades
        </h2>
        <p className="text-[10.5px] px-1 mb-2 leading-snug" style={{ color: "var(--text-secondary)" }}>
          You have these, but not the Shadow, which hits 20% harder. Each one is a Shadow raid away.
        </p>

        {upgrades.length === 0 ? (
          <p
            className="v2-neon-card-soft p-4 text-[11.5px]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="yours-shadows-empty"
          >
            Nothing to chase here: none of the top raid Shadows are ones you already own the
            ordinary version of.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="yours-shadows">
            {upgrades.map(({ row, rank, fills }) => (
              <li
                key={`${row.attacker.dexId}-shadow`}
                className="v2-neon-card-soft px-3 py-2.5 flex items-center gap-3"
                data-testid={`yours-shadow-${row.attacker.dexId}`}
              >
                <PokemonSilhouette id={row.attacker.dexId} size={38} />
                <div className="min-w-0 flex-1">
                  {/* The FORM is named here, unlike on an owned counter.
                      This row is advice about something to go and get, so
                      "the Therian one" is the useful half — it is only a
                      claim about your dex that the form would overstate. */}
                  <p className="text-[12.5px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                    Shadow {row.attacker.name}
                    {row.attacker.form ? ` (${row.attacker.form})` : ""}
                  </p>
                  {/* WHY this one, before what it runs. "Would be your #2 Rock"
                      is the argument for chasing it; "#4 overall" is only a
                      statement about the ranking. */}
                  <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                    <UpgradeReason fills={fills} rank={rank} />
                  </p>
                  <p className="text-[9.5px] truncate" style={{ color: "var(--text-secondary)" }}>
                    {row.fast?.name} · {row.charged?.name}
                  </p>
                </div>
                <span
                  className="shrink-0 text-[8px] font-black uppercase tracking-wide px-1.5 py-px rounded-full"
                  style={{ background: "hsl(280 70% 62% / 0.18)", color: "hsl(280 70% 62%)" }}
                >
                  Shadow
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      </>
      )}
    </>
  );
}

const LEAGUE_TITLE: Record<YoursLeagueKey, string> = {
  great: "Great League",
  ultra: "Ultra League",
  master: "Master League",
};

/** One league's best copies: rank, the copy as art, what it becomes, the standing. */
function YourLeagueBest({ league, picks }: { league: YoursLeagueKey; picks: YourPvpPick[] }) {
  return (
    <div className="v2-neon-card-soft p-3" data-testid={`yours-best-${league}`}>
      <p className="text-[11px] font-bold inline-flex items-center gap-1.5" style={{ color: "var(--text-ink-bright)" }}>
        <LeagueBall league={league} size={16} />
        {LEAGUE_TITLE[league]} top 100
      </p>
      {picks.length === 0 ? (
        <p className="mt-1.5 text-[10.5px]" style={{ color: "var(--text-secondary)" }} data-testid={`yours-best-${league}-empty`}>
          Nothing you hold reaches this top 100 yet.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {picks.map((p) => (
            <li
              key={`${p.entry.dexId}-${p.entry.shadow}-${p.record.id}`}
              className="flex items-center gap-2"
              data-testid={`yours-best-${league}-${p.entry.rank}`}
            >
              <span className="w-8 shrink-0 text-center text-[13px] font-black tabular-nums" style={{ color: "var(--accent-fill)" }}>
                #{p.entry.rank}
              </span>
              {p.record.speciesId != null && (
                <CaughtSprite id={p.record.speciesId} shiny={p.record.isShiny} size={34} />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                  {myPokemonDisplayName(p.record)}
                </p>
                <p className="text-[9.5px] truncate" style={{ color: "var(--text-secondary)" }}>
                  {p.record.ivAtk != null
                    ? `${p.record.ivAtk}/${p.record.ivDef}/${p.record.ivHp}`
                    : "IVs not scanned"}
                  {p.entry.moves.length > 0 ? ` · ${p.entry.moves.join(" + ")}` : ""}
                </p>
              </div>
              {p.asForm && (
                // Judged as what it becomes: the destiny form's art says so.
                <span className="shrink-0 inline-flex items-center gap-1" data-testid={`yours-best-${league}-${p.entry.rank}-as`}>
                  <CaughtSprite id={p.asForm.speciesId} shiny={p.record.isShiny} size={26} />
                </span>
              )}
              <span className="shrink-0 text-[14px] font-black tabular-nums" style={{ color: "var(--accent-fill)" }}>
                {p.percent != null ? `${Math.round(p.percent)}%` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The raid top 100's best copies: rank, the copy, level and IVs, the ranked moves. */
function YourRaidBest({ picks }: { picks: YourRaidPick[] }) {
  return (
    <div className="v2-neon-card-soft p-3" data-testid="yours-best-raid">
      <p className="text-[11px] font-bold" style={{ color: "var(--text-ink-bright)" }}>
        Raid attackers top 100
      </p>
      {picks.length === 0 ? (
        <p className="mt-1.5 text-[10.5px]" style={{ color: "var(--text-secondary)" }} data-testid="yours-best-raid-empty">
          Nothing you hold reaches the raid top 100 yet.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {picks.map((p) => (
            <li
              key={`${p.row.attacker.dexId}-${p.row.attacker.form ?? ""}-${p.row.shadow}-${p.record.id}`}
              className="flex items-center gap-2"
              data-testid={`yours-best-raid-${p.row.rank}`}
            >
              <span className="w-8 shrink-0 text-center text-[13px] font-black tabular-nums" style={{ color: "var(--accent-fill)" }}>
                #{p.row.rank}
              </span>
              {p.record.speciesId != null && (
                <CaughtSprite id={p.record.speciesId} shiny={p.record.isShiny} size={34} />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                  {myPokemonDisplayName(p.record)}
                  {p.row.shadow ? " (Shadow)" : ""}
                </p>
                <p className="text-[9.5px] truncate" style={{ color: "var(--text-secondary)" }}>
                  {p.derived.level != null ? `L${p.derived.level}` : "Level unknown"}
                  {p.ivPercent != null ? ` · ${p.ivPercent}% IVs` : ""}
                </p>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  {p.row.fast && <MoveChip name={p.row.fast.name} type={p.row.fast.type} />}
                  {p.row.charged && <MoveChip name={p.row.charged.name} type={p.row.charged.type} />}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BossCard({ tier, cov }: { tier: RaidTierKey; cov: BossCoverage }) {
  const { boss, ownedCount, outOf, bestOwned, bestMissing } = cov;
  const covered = ownedCount > 0;
  // Only the FORM needs words now: the moves are chips, and a Mega has its own
  // dex so that form is already exact.
  const form = bestOwned?.attacker.form ?? null;
  const formNeeded = form && !form.startsWith("Mega") ? form : null;

  return (
    <div className="v2-neon-card-soft p-3" data-testid={`yours-boss-${boss.speciesId ?? boss.name}`}>
      <div className="flex items-center gap-2.5">
        {typeof boss.speciesId === "number" && <CaughtSprite id={boss.speciesId} size={34} />}
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
            {boss.name}
          </p>
          <p className="text-[9.5px]" style={{ color: "var(--text-secondary)" }}>
            {/* No tier here: the group heading above already says it. */}
            {(boss.types ?? []).map(typeLabel).join(" / ")}
          </p>
        </div>
        <span
          className="shrink-0 text-right"
          title={`Of the top ${outOf} counters against ${boss.name}`}
        >
          <span
            className="block text-[15px] font-black tabular-nums leading-none"
            style={{ color: covered ? "var(--accent-fill)" : "var(--warning-text)" }}
            data-testid={`yours-owned-${boss.speciesId ?? boss.name}`}
          >
            {ownedCount}/{outOf}
          </span>
          <span className="block text-[8px] font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--text-secondary)" }}>
            Counters
          </span>
        </span>
      </div>

      {/* The one to build, and the moveset it needs, which is the whole
          reason ownership alone is not the answer.

          SPRITE AND CHIPS, not a sentence. The card already knows who it is
          talking about, so showing the Pokemon with its two moves as
          type-coloured pills reads at a glance where the prose had to be read.
          The form stays as words because it is a condition, not a move. */}
      {bestOwned && (
        <div
          className="mt-2.5 rounded-[var(--radius-md)] px-2.5 py-2"
          style={{ background: "var(--accent-fill-glow-faint)", border: "1px solid var(--accent-fill-glow)" }}
          data-testid={`yours-build-${boss.speciesId ?? boss.name}`}
        >
          <p className="text-[10px] font-bold flex items-center gap-1 uppercase tracking-wide" style={{ color: "var(--brand-green-on-light)" }}>
            <Check className="w-3 h-3 shrink-0" aria-hidden />
            Your best counter
          </p>
          <div className="mt-1 flex items-center gap-2">
            <CaughtSprite id={bestOwned.attacker.dexId} size={38} />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-bold truncate" style={{ color: "var(--text-primary)" }}>
                {bestOwned.name}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {bestOwned.fast && <MoveChip name={bestOwned.fast.name} type={bestOwned.fast.type} />}
                {bestOwned.charged && (
                  <MoveChip name={bestOwned.charged.name} type={bestOwned.charged.type} />
                )}
              </div>
            </div>
          </div>
          {formNeeded && (
            <p className="mt-1.5 text-[10px] leading-snug" style={{ color: "var(--text-secondary)" }}>
              Yours must be the {formNeeded} form.
            </p>
          )}
        </div>
      )}

      {bestMissing && (
        <p
          className="mt-2 text-[10px] leading-snug flex items-start gap-1"
          style={{ color: "var(--text-secondary)" }}
          data-testid={`yours-missing-${boss.speciesId ?? boss.name}`}
        >
          <Sparkles className="w-3 h-3 shrink-0 mt-px" aria-hidden />
          <span>
            Best one you are missing: <strong style={{ color: "var(--text-secondary)" }}>{bestMissing.name}</strong>
            {bestMissing.fast && bestMissing.charged
              ? ` (${bestMissing.fast.name} · ${bestMissing.charged.name})`
              : ""}
            .
          </span>
        </p>
      )}

      {!bestOwned && (
        <p
          className="mt-2 text-[10px]"
          style={{ color: "var(--warning-text)" }}
          data-testid={`yours-none-${boss.speciesId ?? boss.name}`}
        >
          You do not own any of the top {outOf} counters for this one yet.
        </p>
      )}
    </div>
  );
}

/**
 * One type's readiness.
 *
 * ONE HUE, VARYING STRENGTH — not each type in its own colour. The cell is
 * comparing a quantity across eighteen cells, and eighteen different hues at
 * eighteen different luminances cannot be read against each other at all. The
 * type's own colour survives as a dot, which is identity rather than measure.
 */
function TypeCell({
  cov,
  mode,
  selected,
  onSelect,
}: {
  cov: TypeCoverage;
  mode: TypeCoverageMode;
  selected: boolean;
  onSelect: () => void;
}) {
  const empty = cov.ownedCount === 0;
  // A floor of 8% so a covered-but-thin cell still reads as filled rather than
  // blank; empty gets the warning treatment instead, because "none" is a
  // different state to "few" and should not just be a paler version of it.
  //
  // CEILING OF 36%, WHICH IS THE READABILITY FIX. The ramp used to reach 75%,
  // and on dark theme --accent-fill is neon (#00FF9D): a nearly-full cell
  // became a bright green slab that the grey text tokens could not sit on, and
  // a full grid of them was a wall. Kept low, the cell stays a tint of the
  // surface in BOTH themes, so --text-primary reads on it either way. The ramp
  // is secondary encoding anyway; every cell prints its own number.
  const fill = empty ? 0 : 0.08 + cov.ratio * 0.28;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={`yours-type-${cov.type}`}
      title={
        mode === "attackers"
          ? `${typeLabel(cov.type)}: you own ${cov.ownedCount} of the top ${cov.outOf} ${typeLabel(cov.type)} attackers`
          : `${typeLabel(cov.type)}: you own ${cov.ownedCount} of the top ${cov.outOf} counters against ${typeLabel(cov.type)} bosses`
      }
      className="rounded-[var(--radius-md)] px-2 py-1.5 text-left transition-colors [transition-duration:var(--duration-fast)]"
      style={{
        background: empty
          ? "var(--warning-subtle)"
          : `color-mix(in srgb, var(--accent-fill) ${Math.round(fill * 100)}%, transparent)`,
        border: `1px solid ${
          selected
            ? "var(--accent-fill)"
            : empty
              ? "var(--warning-fill)"
              : "var(--border-subtle)"
        }`,
      }}
    >
      <span className="flex items-center gap-1">
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: typeColor(cov.type) }}
          aria-hidden
        />
        <span
          className="text-[9.5px] font-bold uppercase tracking-wide truncate"
          style={{ color: "var(--text-primary)" }}
        >
          {typeLabel(cov.type)}
        </span>
      </span>
      <span
        className="block text-[13px] font-black tabular-nums leading-none mt-0.5"
        style={{ color: empty ? "var(--warning-text)" : "var(--text-ink-bright)" }}
      >
        {cov.ownedCount}
        <span className="text-[9.5px] font-bold" style={{ color: "var(--text-secondary)" }}>
          /{cov.outOf}
        </span>
      </span>
    </button>
  );
}

/**
 * The tapped type, spelled out: the podium, and what of it you have.
 *
 * TEN, NOT ONE. A single "your best" answered half the question and only for
 * the half you already had. The type's top ten, each ranked and marked, says
 * what the answer IS and which part of it is yours in the same glance, which
 * is what a trainer opening a thin type actually wants. (Three until
 * 2026-09-10: the owner asked for the full ten once ownership read off My
 * Pokémon, the comparison being the strong half.)
 */
function TypeDetail({
  cov,
  mode,
  isPro,
  onUpsell,
}: {
  cov: TypeCoverage;
  mode: TypeCoverageMode;
  isPro: boolean;
  onUpsell: () => void;
}) {
  // Their best only needs its own line when it is NOT on the podium; otherwise
  // it is already marked up there and repeating it is noise.
  const bestOutsideTop =
    cov.bestOwned && !cov.top.some((t) => t.owned && t.row.name === cov.bestOwned!.name)
      ? cov.bestOwned
      : null;
  const bestRankOutside = bestOutsideTop
    ? // Its position within the whole ranking this cell was measured over.
      cov.top.length + 1
    : null;

  return (
    <div className="mt-2 v2-neon-card-soft p-3" data-testid={`yours-type-detail-${cov.type}`} data-mode={mode}>
      <p className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: "var(--accent-fill)" }} data-testid={`yours-type-detail-${cov.type}-kicker`}>
        {mode === "attackers" ? `Best ${typeLabel(cov.type)} attackers` : `Best counters against ${typeLabel(cov.type)}`}
      </p>
      <p className="mt-0.5 text-[11px] font-bold" style={{ color: "var(--text-ink-bright)" }} data-testid={`yours-type-detail-${cov.type}-heading`}>
        {mode === "attackers"
          ? `Top ${cov.outOf} ${typeLabel(cov.type)}-type attackers: you own ${cov.ownedCount}`
          : `Top ${cov.outOf} against ${typeLabel(cov.type)} bosses: you own ${cov.ownedCount}`}
      </p>

      <ul className="mt-2 space-y-1.5" data-testid={`yours-type-podium-${cov.type}`}>
        {/* #1 IS FREE, #2 TO #10 ARE PRO (2026-09-10 owner direction: only
            first place shows for Basic, and the rest are not on the page at
            all; one tease below stands for them). Showing the best counter
            outright means the free page still answers the question. */}
        {(isPro ? cov.top : cov.top.slice(0, 1)).map(({ row, owned }, i) => (
          <li
            /**
             * THE FORM BELONGS IN THIS KEY.
             *
             * It was `dexId-shadow`, which is not unique: a species with two
             * battle forms collides with itself. The Dragon podium is exactly
             * that case, holding both Palkia (Origin) (Shadow) and Palkia
             * (Shadow) at 484-true. React cannot tell those two rows apart, so
             * moving to another type left a stale Palkia behind and renumbered
             * the rest, which is how a Dragon counter turned up on the Water
             * panel with dragon moves against a water boss.
             *
             * The ranking was right the whole time; only the reconciliation
             * was wrong. Guarded by a test over every type.
             */
            key={`${row.attacker.dexId}-${row.attacker.form ?? ""}-${row.shadow}`}
            className="flex items-center gap-2"
            data-testid={`yours-podium-${cov.type}-${i + 1}`}
          >
            {/* The rank, on the left of each row. */}
            <span
              className="w-6 shrink-0 text-center text-[12px] font-black tabular-nums"
              style={{ color: owned ? "var(--accent-fill)" : "var(--text-secondary)" }}
            >
              #{i + 1}
            </span>
            <CaughtSprite id={row.attacker.dexId} size={32} />
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] font-bold truncate" style={{ color: "var(--text-primary)" }}>
                {row.name}
              </p>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {row.fast && <MoveChip name={row.fast.name} type={row.fast.type} />}
                {row.charged && <MoveChip name={row.charged.name} type={row.charged.type} />}
              </div>
            </div>
            {/* Owned or not, said plainly, because the whole podium is shown
                whether or not any of it is theirs. */}
            <span
              className="shrink-0 text-[8px] font-black uppercase tracking-wide px-1.5 py-px rounded-full"
              style={
                owned
                  ? { background: "var(--accent-fill-glow-faint)", color: "var(--brand-green-on-light)" }
                  : { background: "var(--surface-sunken)", color: "var(--text-secondary)" }
              }
              data-testid={`yours-podium-${cov.type}-${i + 1}-${owned ? "owned" : "missing"}`}
            >
              {owned ? "Yours" : "Missing"}
            </span>
            <MetaSearchButton
              row={{
                dexId: row.attacker.dexId,
                shadow: row.shadow,
                mega: row.attacker.form?.startsWith("Mega") ?? false,
              }}
              label={row.name}
              testId={`yours-podium-${cov.type}-${i + 1}-search`}
            />
          </li>
        ))}
      </ul>

      {!isPro && cov.top.length > 1 && (
        <button
          type="button"
          onClick={onUpsell}
          data-testid={`yours-podium-upsell-${cov.type}`}
          className="mt-2 w-full rounded-[var(--radius-md)] px-2.5 py-2 text-left"
          style={{
            background: "var(--accent-fill-glow-faint)",
            border: "1px solid var(--accent-fill-glow)",
          }}
        >
          <span className="text-[10.5px] font-bold flex items-center gap-1" style={{ color: "var(--brand-green-on-light)" }}>
            <Lock className="w-3 h-3 shrink-0" aria-hidden />
            See #2 to #10 with Pro
          </span>
          <span className="mt-0.5 block text-[10px] leading-snug" style={{ color: "var(--text-secondary)" }}>
            Number one is always free. Pro shows the other nine for every type, each marked yours or missing.
          </span>
        </button>
      )}

      {bestOutsideTop && (
        <p className="mt-2 text-[10px] leading-snug" style={{ color: "var(--text-secondary)" }}>
          Your best is further down: <strong style={{ color: "var(--text-primary)" }}>{bestOutsideTop.name}</strong>
          {bestRankOutside ? `, outside the top ${cov.top.length}` : ""}.
        </p>
      )}

      {cov.ownedCount === 0 && (
        <p className="mt-2 text-[10px]" style={{ color: "var(--warning-text)" }}>
          You do not own any of the top {cov.outOf} for this type.
        </p>
      )}
    </div>
  );
}

/**
 * Why a Shadow is worth chasing.
 *
 * The gap it fills leads, because that is the argument: "would be your #2 Rock
 * counter, and you have one" tells a trainer something about THEIR collection.
 * "#4 overall" only tells them about the ranking, which the Raids tab already
 * shows in full. The overall rank stays as the fallback for a Shadow that is
 * simply strong without plugging a hole.
 */
function UpgradeReason({
  fills,
  rank,
}: {
  fills: ShadowUpgrade["fills"];
  rank: number;
}) {
  if (!fills) return <>#{rank} overall</>;
  return (
    <>
      Would be your #{fills.rankInType} {typeLabel(fills.type)} counter
      {fills.ownedInType === 0 ? (
        <>, you have none</>
      ) : (
        <>
          {" "}
          and you have {fills.ownedInType} of {fills.outOf}
        </>
      )}
    </>
  );
}
